import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import "./BasketballGamePublic.css";

function BasketballGamePublic({
  game,
  theme,
  slug,
  statValues,
  periodScores,
}) {
  const navigate = useNavigate();

  const visiblePeriods = (periodScores || []).filter(
    (period) =>
      period?.gamePeriod &&
      !period.gamePeriod.hidden &&
      period.gamePeriod.name !== "Final"
  );

  const showPeriodsTable = visiblePeriods.length > 0;

  const finalPeriod = periodScores.find(
    (period) => period.gamePeriod?.name === "Final"
  );

  const formatYouTubeEmbedUrl = (url) => {
    if (!url) return "";

    if (url.includes("youtube.com/watch?v=")) {
      return url.replace("watch?v=", "embed/");
    }

    if (url.includes("youtu.be/")) {
      return url.replace("youtu.be/", "www.youtube.com/embed/");
    }

    return url;
  };

  return (
    <div className={`basketballGamePublic-container basketballGamePublic-theme-${theme}`}>
      {/* Game Info */}
      <div className="basketballGamePublic-info">
        <h4>{game.location || "Location TBA"}</h4>
        <h5>
          {game.date
            ? formatInTimeZone(
              new Date(`${game.date}T${game.time || "00:00"}`),
              "America/Los_Angeles",
              "MMM d, yyyy • h:mm a"
            )
            : "Date TBA"}
        </h5>
      </div>

      <div className="basketballGamePublic-periods-container">
        <div className="basketballGamePublic-periods-final-score">
          <NavLink
            to={slug ? `/${slug}/teams/${game.homeTeam?.id}` : `/teams/${game.homeTeam?.id}`}
            style={{ textDecoration: "none", color: "black" }}
          >
            <h5>{game.homeTeam?.name}</h5>
          </NavLink>

          <h5>
            {finalPeriod?.period_score_team1 !== undefined
              ? finalPeriod.period_score_team1
              : game.score_home}
          </h5>

          <h5>FINAL</h5>

          <h5>
            {finalPeriod?.period_score_team2 !== undefined
              ? finalPeriod.period_score_team2
              : game.score_away}
          </h5>

          <NavLink
            to={slug ? `/${slug}/teams/${game.awayTeam?.id}` : `/teams/${game.awayTeam?.id}`}
            style={{ textDecoration: "none", color: "black" }}
          >
            <h5>{game.awayTeam?.name}</h5>
          </NavLink>
        </div>

        {showPeriodsTable && (
          <table className="basketballGamePublic-periods-table">
            <thead>
              <tr>
                <th></th>
                {visiblePeriods.map((period) => (
                  <th key={period.id}>{period.gamePeriod?.name}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>{game.homeTeam?.name}</td>
                {visiblePeriods.map((period) => (
                  <td key={period.id}>{period.period_score_team1}</td>
                ))}
              </tr>

              <tr>
                <td>{game.awayTeam?.name}</td>
                {visiblePeriods.map((period) => (
                  <td key={period.id}>{period.period_score_team2}</td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div className="basketballGamePublic-video-container">
        {game.video_url && (
          <div className="basketballGamePublic-video-box">
            <iframe
              width="100%"
              height="315"
              src={formatYouTubeEmbedUrl(game.video_url)}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Game Video"
            />
          </div>
        )}
      </div>

      {/* Home Team Stats */}
      <div className="basketballTeamPublic-container-home">
        <h3 className="basketballTeamPublic-header">
          Home Team: {game.homeTeam?.name} (
          {finalPeriod?.period_score_team1 ?? game.score_home ?? 0})
        </h3>

        <table className="basketballGamePublic-stat-table">
          <thead>
            <tr>
              <th className="basketballPlayerPublic-column">Player</th>
              {game.stats.map((stat) => (
                <th key={stat.id} className="basketballStatPublic-column">
                  {stat.abbreviation}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {game.homeTeam?.players.map((player) => (
              <tr key={player.id}>
                <td
                  className="basketballPlayerPublic-name"
                  onClick={() =>
                    navigate(
                      slug
                        ? `/${slug}/players/${player.id}`
                        : `/players/${player.id}`
                    )
                  }
                >
                  {player.firstName} {player.lastName}
                </td>

                {game.stats.map((stat) => (
                  <td key={stat.id}>
                    <span className="basketballStatPublic-value">
                      {(() => {
                        const getStatId = (abbreviation) =>
                          game.stats.find((s) => s.abbreviation === abbreviation)?.id;

                        const get = (abbreviation) =>
                          statValues[
                          `${player.roster_id || player.id}-${getStatId(abbreviation)}`
                          ] || 0;
                        if (stat.abbreviation === "FGM") {
                          const twoPMId = getStatId("2PM");
                          const threePMId = getStatId("3PM");

                          if (twoPMId || threePMId) {
                            const twoPM = twoPMId ? get("2PM") : 0;
                            const threePM = threePMId ? get("3PM") : 0;
                            return twoPM + threePM;
                          }

                          return get("FGM");
                        }

                        if (stat.abbreviation === "FGA") {
                          const twoPAId = getStatId("2PA");
                          const threePAId = getStatId("3PA");

                          if (twoPAId || threePAId) {
                            const twoPA = twoPAId ? get("2PA") : 0;
                            const threePA = threePAId ? get("3PA") : 0;
                            return twoPA + threePA;
                          }

                          return get("FGA");
                        }

                        if (stat.abbreviation === "FG%") {
                          const fgm = get("FGM");
                          const fga = get("FGA");
                          return fga ? ((fgm / fga) * 100).toFixed(1) : "0";
                        }

                        if (stat.abbreviation === "FT%") {
                          const ftm = get("FTM");
                          const fta = get("FTA");
                          return fta ? ((ftm / fta) * 100).toFixed(1) : "0";
                        }

                        if (stat.abbreviation === "2P%") {
                          const made = get("2PM");
                          const att = get("2PA");
                          return att ? ((made / att) * 100).toFixed(1) : "0";
                        }

                        if (stat.abbreviation === "3P%") {
                          const made = get("3PM");
                          const att = get("3PA");
                          return att ? ((made / att) * 100).toFixed(1) : "0";
                        }

                        return statValues[`${player.roster_id || player.id}-${stat.id}`] || 0;
                      })()}
                    </span>
                  </td>
                ))}
              </tr>
            ))}

            <tr className="basketballTotalPublic-row">
              <td className="basketballPlayerPublic-name">
                <strong>Total</strong>
              </td>

              {game.stats.map((stat) => {
                const getStatId = (abbr) =>
                  game.stats.find((s) => s.abbreviation === abbr)?.id;

                const get = (rosterId, abbr) =>
                  statValues[`${rosterId}-${getStatId(abbr)}`] || 0;

                if (["FG%", "FT%", "2P%", "3P%"].includes(stat.abbreviation)) {
                  let percent = null;

                  if (stat.abbreviation === "FG%") {
                    const fgm = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FGM"),
                      0
                    );
                    const fga = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FGA"),
                      0
                    );
                    percent = fga ? (fgm / fga) * 100 : null;
                  }

                  if (stat.abbreviation === "FT%") {
                    const ftm = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FTM"),
                      0
                    );
                    const fta = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FTA"),
                      0
                    );
                    percent = fta ? (ftm / fta) * 100 : null;
                  }

                  if (stat.abbreviation === "2P%") {
                    const made = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "2PM"),
                      0
                    );
                    const att = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "2PA"),
                      0
                    );
                    percent = att ? (made / att) * 100 : null;
                  }

                  if (stat.abbreviation === "3P%") {
                    const made = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "3PM"),
                      0
                    );
                    const att = (game.homeTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "3PA"),
                      0
                    );
                    percent = att ? (made / att) * 100 : null;
                  }

                  return (
                    <td key={stat.id}>
                      <strong>{percent !== null ? percent.toFixed(1) : "0"}</strong>
                    </td>
                  );
                }

                let total = 0;

                if (stat.abbreviation === "FGM") {
                  const twoPM = (game.homeTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "2PM"),
                    0
                  );
                  const threePM = (game.homeTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "3PM"),
                    0
                  );
                  total = twoPM + threePM;
                } else if (stat.abbreviation === "FGA") {
                  const twoPA = (game.homeTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "2PA"),
                    0
                  );
                  const threePA = (game.homeTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "3PA"),
                    0
                  );
                  total = twoPA + threePA;
                } else {
                  total = (game.homeTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, stat.abbreviation),
                    0
                  );
                }

                return (
                  <td key={stat.id}>
                    <strong>{total}</strong>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Away Team Stats */}
      <div className="basketballTeamPublic-container-away">
        <h3 className="basketballTeamPublic-header">
          Away Team: {game.awayTeam?.name} (
          {finalPeriod?.period_score_team2 ?? game.score_away ?? 0})
        </h3>

        <table className="basketballGamePublic-stat-table">
          <thead>
            <tr>
              <th className="basketballPlayerPublic-column">Player</th>
              {game.stats.map((stat) => (
                <th key={stat.id} className="basketballStatPublic-column">
                  {stat.abbreviation}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {game.awayTeam?.players.map((player) => (
              <tr key={player.id}>
                <td
                  className="basketballPlayerPublic-name"
                  onClick={() =>
                    navigate(
                      slug
                        ? `/${slug}/players/${player.id}`
                        : `/players/${player.id}`
                    )
                  }
                >
                  {player.firstName} {player.lastName}
                </td>

                {game.stats.map((stat) => (
                  <td key={stat.id}>
                    <span className="basketballStatPublic-value">
                      {(() => {
                        const getStatId = (abbreviation) =>
                          game.stats.find((s) => s.abbreviation === abbreviation)?.id;

                        const get = (abbreviation) =>
                          statValues[
                          `${player.roster_id || player.id}-${getStatId(abbreviation)}`
                          ] || 0;
                        if (stat.abbreviation === "FGM") {
                          const twoPMId = getStatId("2PM");
                          const threePMId = getStatId("3PM");

                          if (twoPMId || threePMId) {
                            const twoPM = twoPMId ? get("2PM") : 0;
                            const threePM = threePMId ? get("3PM") : 0;
                            return twoPM + threePM;
                          }

                          return get("FGM");
                        }

                        if (stat.abbreviation === "FGA") {
                          const twoPAId = getStatId("2PA");
                          const threePAId = getStatId("3PA");

                          if (twoPAId || threePAId) {
                            const twoPA = twoPAId ? get("2PA") : 0;
                            const threePA = threePAId ? get("3PA") : 0;
                            return twoPA + threePA;
                          }

                          return get("FGA");
                        }

                        if (stat.abbreviation === "FG%") {
                          const fgm = get("FGM");
                          const fga = get("FGA");
                          return fga ? ((fgm / fga) * 100).toFixed(1) : "0";
                        }

                        if (stat.abbreviation === "FT%") {
                          const ftm = get("FTM");
                          const fta = get("FTA");
                          return fta ? ((ftm / fta) * 100).toFixed(1) : "0";
                        }

                        if (stat.abbreviation === "2P%") {
                          const made = get("2PM");
                          const att = get("2PA");
                          return att ? ((made / att) * 100).toFixed(1) : "0";
                        }

                        if (stat.abbreviation === "3P%") {
                          const made = get("3PM");
                          const att = get("3PA");
                          return att ? ((made / att) * 100).toFixed(1) : "0";
                        }

                        return statValues[`${player.roster_id || player.id}-${stat.id}`] || 0;
                      })()}
                    </span>
                  </td>
                ))}
              </tr>
            ))}

            <tr className="basketballTotalPublic-row">
              <td className="basketballPlayerPublic-name">
                <strong>Total</strong>
              </td>

              {game.stats.map((stat) => {
                const getStatId = (abbr) =>
                  game.stats.find((s) => s.abbreviation === abbr)?.id;

                const get = (rosterId, abbr) =>
                  statValues[`${rosterId}-${getStatId(abbr)}`] || 0;

                if (["FG%", "FT%", "2P%", "3P%"].includes(stat.abbreviation)) {
                  let percent = null;

                  if (stat.abbreviation === "FG%") {
                    const fgm = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FGM"),
                      0
                    );
                    const fga = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FGA"),
                      0
                    );
                    percent = fga ? (fgm / fga) * 100 : null;
                  }

                  if (stat.abbreviation === "FT%") {
                    const ftm = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FTM"),
                      0
                    );
                    const fta = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "FTA"),
                      0
                    );
                    percent = fta ? (ftm / fta) * 100 : null;
                  }

                  if (stat.abbreviation === "2P%") {
                    const made = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "2PM"),
                      0
                    );
                    const att = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "2PA"),
                      0
                    );
                    percent = att ? (made / att) * 100 : null;
                  }

                  if (stat.abbreviation === "3P%") {
                    const made = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "3PM"),
                      0
                    );
                    const att = (game.awayTeam?.players || []).reduce(
                      (sum, p) => sum + get(p.roster_id || p.id, "3PA"),
                      0
                    );
                    percent = att ? (made / att) * 100 : null;
                  }

                  return (
                    <td key={stat.id}>
                      <strong>{percent !== null ? percent.toFixed(1) : "0"}</strong>
                    </td>
                  );
                }

                let total = 0;

                if (stat.abbreviation === "FGM") {
                  const twoPM = (game.awayTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "2PM"),
                    0
                  );
                  const threePM = (game.awayTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "3PM"),
                    0
                  );
                  total =
                    twoPM || threePM
                      ? twoPM + threePM
                    : (game.awayTeam?.players || []).reduce(
                        (sum, p) => sum + get(p.roster_id || p.id, "FGM"),
                        0
                      );
                } else if (stat.abbreviation === "FGA") {
                  const twoPA = (game.awayTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "2PA"),
                    0
                  );
                  const threePA = (game.awayTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, "3PA"),
                    0
                  );
                  total =
                    twoPA || threePA
                      ? twoPA + threePA
                    : (game.awayTeam?.players || []).reduce(
                        (sum, p) => sum + get(p.roster_id || p.id, "FGA"),
                        0
                      );
                } else {
                  total = (game.awayTeam?.players || []).reduce(
                    (sum, p) => sum + get(p.roster_id || p.id, stat.abbreviation),
                    0
                  );
                }

                return (
                  <td key={stat.id}>
                    <strong>{total}</strong>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BasketballGamePublic;